import React, { useState, useEffect } from 'react';
import { hasAnyArrayData } from '../../utils/profileVisibility';
import SectionEditModal from './SectionEditModal';
import EditIconButton from './EditIconButton';

function BrandPartnersSection({ brandPartners, isOwner, onSaveBrandPartners, mode = 'edit' }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formPartners, setFormPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ name: '', logoUrl: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isModalOpen) {
      const initial = (brandPartners || []).map((p) => ({
        id: p.id || String(p.name || '') || String(Date.now() + Math.random()),
        name: p.name || '',
        logoUrl: p.logoUrl || '',
      }));
      setFormPartners(initial);
      setNewPartner({ name: '', logoUrl: '' });
      setError(null);
    }
  }, [brandPartners, isModalOpen]);

  const handleAddNewPartner = () => {
    const trimmedName = newPartner.name.trim();
    if (!trimmedName) {
      setError('Brand name is required to add a partner.');
      return;
    }

    const id = String(Date.now() + Math.random());
    setFormPartners((prev) => [
      ...prev,
      {
        id,
        name: trimmedName,
        logoUrl: newPartner.logoUrl.trim(),
      },
    ]);

    setNewPartner({ name: '', logoUrl: '' });
    setError(null);
  };

  const handleRemovePartner = (id) => {
    setFormPartners((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSave = async () => {
    setError(null);

    // Basic validation: all names required
    // Remove undefined values - Firestore doesn't accept undefined
    const cleaned = formPartners
      .map((p) => {
        const logoUrl = (p.logoUrl || '').trim();
        const partner = {
          id: p.id || String(Date.now() + Math.random()),
          name: (p.name || '').trim(),
        };
        // Only include logoUrl if it has a value (don't include undefined or empty string)
        if (logoUrl) {
          partner.logoUrl = logoUrl;
        }
        return partner;
      })
      .filter((p) => p.name.length > 0);

    if (cleaned.length === 0 && (brandPartners || []).length > 0) {
      // Optional: allow clearing all, but you can also block it if desired
    }

    try {
      setIsSaving(true);
      await onSaveBrandPartners(cleaned);
      setIsModalOpen(false);
      setError(null);
    } catch (e) {
      setError(`Failed to save brand partners: ${e.message || 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError(null);
    // Reset form from source
    const reset = (brandPartners || []).map((p) => ({
      id: p.id || String(p.name || '') || String(Date.now() + Math.random()),
      name: p.name || '',
      logoUrl: p.logoUrl || '',
    }));
    setFormPartners(reset);
    setNewPartner({ name: '', logoUrl: '' });
  };

  // VIEW MODE
  const renderViewMode = () => {
    const hasPartners = hasAnyArrayData(brandPartners);

    // Hide section if empty in public mode
    if (mode === 'public' && !hasPartners) {
      return null;
    }

    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 mb-5 md:mb-6 relative">
        <div className="mb-3 md:mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Brand Partners
          </h2>
        </div>
        {isOwner && (
          <div className="absolute right-4 sm:right-6 top-4 sm:top-6 z-10">
            <EditIconButton onClick={() => setIsModalOpen(true)} />
          </div>
        )}

        {hasPartners ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {brandPartners.map((partner) => {
              const initial = (partner.name || '').trim().charAt(0).toUpperCase();

              return (
                <div
                  key={partner.id || partner.name}
                  className="flex flex-col items-center text-center gap-2"
                >
                  <div className="p-3 bg-white border border-slate-200 rounded-lg shadow-sm flex items-center justify-center transition-shadow transition-transform duration-200 ease-out hover:shadow-md hover:scale-[1.03]">
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={partner.name || 'Brand logo'}
                        className="h-12 w-auto max-w-[120px] object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="text-sm font-semibold text-slate-600">
                        {initial || '?'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-700 truncate max-w-[6rem]">
                    {partner.name || 'Unnamed'}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-700">
            No brand partners added yet.
            {isOwner && " Add brands you've worked with to build trust with potential partners."}
          </p>
        )}
      </section>
    );
  };

  // EDIT MODE - Render form content for modal
  const renderEditForm = () => {
    return (
      <div className="space-y-4">
        {/* Existing partners */}
        <div className="space-y-3">
          {formPartners.length > 0 ? (
            formPartners.map((partner, idx) => (
              <div
                key={partner.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 border border-slate-200 rounded-md p-3"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={partner.name || 'Brand logo'}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="text-xs font-semibold text-slate-600">
                        {(partner.name || '').trim().charAt(0).toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div>
                      <label className="text-xs font-medium text-slate-600">
                        Brand name
                      </label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={partner.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormPartners((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], name: value };
                            return copy;
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">
                        Logo URL (optional)
                      </label>
                      <input
                        type="url"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={partner.logoUrl}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormPartners((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], logoUrl: value };
                            return copy;
                          });
                        }}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
                <div className="flex sm:flex-col items-center gap-2 self-stretch sm:self-auto">
                  <button
                    type="button"
                    onClick={() => handleRemovePartner(partner.id)}
                    className="text-xs font-medium text-red-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">
              You haven't added any brand partners yet. Start by adding one below.
            </p>
          )}
        </div>

        {/* Add new partner */}
        <div className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/60">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Add new partner
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">
                Brand name
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newPartner.name}
                onChange={(e) =>
                  setNewPartner((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g. Glossier, Nike, Airbnb"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">
                Logo URL (optional)
              </label>
              <input
                type="url"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newPartner.logoUrl}
                onChange={(e) =>
                  setNewPartner((prev) => ({ ...prev, logoUrl: e.target.value }))
                }
                placeholder="https://..."
              />
            </div>
          </div>
          <div>
            <button
              type="button"
              onClick={handleAddNewPartner}
              className="mt-2 inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
            >
              Add partner
            </button>
          </div>
        </div>

      </div>
    );
  };

  return (
    <>
      {renderViewMode()}
      
      {isOwner && (
        <SectionEditModal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title="Edit Brand Partners"
          subtitle="Add the brands you've worked with to build credibility."
          onSave={handleSave}
          isSaving={isSaving}
          errors={error ? { _general: error } : {}}
        >
          {renderEditForm()}
        </SectionEditModal>
      )}
    </>
  );
}

export default BrandPartnersSection;
